import { matchPath, useLocation } from 'react-router';
import { generatePath } from '@klpkonsern/routing';
import { AppRoutes } from '@klpkonsern/routing';

import { useAppParamsContext } from './useAppParamsContext';
import {
    clearStorage,
    getFromStorage,
    saveToStorage,
} from '../../utils/storage';
import { ParamType } from '@forvalter/utils/ParamType';

export const useAppParams = () => {
    const { appParams, setAppParams } = useAppParamsContext();
    const location = useLocation();

    const getParam = <T>(prop: string): T | undefined => {
        const data = getFromStorage<T>(prop);
        return data !== undefined ? data : appParams[prop];
    };

    const storeParam = <T>(prop: string, value: T) => {
        if (value === undefined || value === null) {
            clearStorage(prop);
        } else {
            saveToStorage(prop, value);
        }
        setAppParams({ [prop]: value });
    };

    const clearParam = (prop: string) => {
        clearStorage(prop);
        setAppParams({ [prop]: undefined });
    };

    const matchRoute = (...fragments: Array<string>) => {
        const path = generatePath(...fragments);

        return !!matchPath(
            location.pathname.replace(AppRoutes.mainPage(), ''),
            { path: path.replace(AppRoutes.mainPage(), '') },
        );
    };

    return {
        get storedBimsyncModelId() {
            return getParam(ParamType.storedBimsyncModelId);
        },
        set storedBimsyncModelId(value: string | undefined) {
            storeParam(ParamType.storedBimsyncModelId, value);
        },

        get byggNummer() {
            return getParam(ParamType.byggNummer);
        },
        set byggNummer(value: string | undefined) {
            storeParam(ParamType.byggNummer, value);
        },

        get invoiceListFilter() {
            return getParam(ParamType.invoiceListFilter);
        },
        set invoiceListFilter(value: object | undefined) {
            storeParam(ParamType.invoiceListFilter, value);
        },

        get isAdmin() {
            return getParam(ParamType.isAdmin);
        },
        set isAdmin(value: boolean | undefined) {
            storeParam(ParamType.isAdmin, value);
        },

        get kundeNummer() {
            return getParam<string | undefined>(ParamType.kundeNummer);
        },

        get lang() {
            return getParam(ParamType.lang);
        },
        set lang(value: string | undefined) {
            storeParam(ParamType.lang, value);
        },

        get messageId() {
            return getParam(ParamType.messageId);
        },
        set messageId(value: string | undefined) {
            storeParam(ParamType.messageId, value);
        },

        get messageListFilter() {
            return getParam(ParamType.messageListFilter);
        },
        set messageListFilter(value: object | undefined) {
            storeParam(ParamType.messageListFilter, value);
        },

        get tab() {
            return getParam(ParamType.tab);
        },
        set tab(value: string | undefined) {
            storeParam(ParamType.tab, value);
        },

        get referrer() {
            return getParam<string>(ParamType.referrer) || '';
        },
        set referrer(value: string | undefined) {
            value
                ? storeParam(ParamType.referrer, value)
                : clearParam(ParamType.referrer);
        },

        get sakId() {
            return getParam(ParamType.sakId);
        },
        set sakId(value: string | undefined) {
            storeParam(ParamType.sakId, value);
        },

        get contractPath() {
            return getParam(ParamType.contractPath);
        },
        set contractPath(value: string | undefined) {
            storeParam(ParamType.contractPath, value);
        },
        storeParam,
        matchRoute,
        clearParam,
    } as const;
};
